/**
 * 关键词与在售产品的字面相关性(0925)—— 抽屉里「这个词和你在售产品不相关」那句提示的**唯一判据**。
 *
 * ★ 0923 测试机:销帮租户(卖 GEO / 400 电话 / 企业 AI 智脑)把微信搜一搜的词整批换成「商标注册」
 *   一类,156 篇全判 0。精判是拿**在售产品**去判「是不是客户」的,词和产品对不上,拉回来的多半是
 *   同行内容 —— 这件事在用户加词的那一刻就能看出来,不必等花一轮再说。
 * ★ 纯字面、不调 LLM:只提示不拦,误判的代价是一行灰字。所以宁可放过(判成相关)也不乱报:
 *   - 产品数据为空(多数租户没建产品树)⇒ 判不了,返回 null,**不提示**;
 *   - 「怎么办 / 多少钱 / 推荐」这类客户口头词不算重合(几乎每个词都带,算进去等于永远相关)。
 * ★ 切词:汉字按相邻两字(bigram)切,字母数字按整段(≥2 位,不分大小写)切。
 *   「400电话多少钱」→ 400 / 电话 / 话多 / …,与「400 电话」的 400、电话 重合 ⇒ 相关。
 */

export const KEYWORD_IRRELEVANT_HINT = "这个词和你在售产品不相关，搜到的多半是同行内容";

/** 词表来源:产品名 / 一句话简介 / AI 按产品给的词(卖点关键词)。 */
export interface ProductVocabInput {
  name: string;
  summary?: string | null;
}

/** 客户口头词与泛词:出现在几乎所有关键词里,不能拿来当「和产品有关」的证据。 */
const GENERIC_TOKENS: ReadonlySet<string> = new Set([
  "怎么",
  "么办",
  "多少",
  "少钱",
  "推荐",
  "哪家",
  "家好",
  "如何",
  "什么",
  "可以",
  "需要",
  "办理",
  "服务",
  "企业",
  "公司",
  "有限",
  "价格",
  "费用",
  "哪里",
  "靠谱",
]);

const CJK = /[一-鿿]/;

/** 一段文字 → 切词集合(见头注的切法)。 */
export function tokenize(text: string | null | undefined): Set<string> {
  const out = new Set<string>();
  const s = (text ?? "").toLowerCase();
  let cjkRun = "";
  let alnumRun = "";
  const flushCjk = () => {
    for (let i = 0; i + 1 < cjkRun.length; i++) out.add(cjkRun.slice(i, i + 2));
    cjkRun = "";
  };
  const flushAlnum = () => {
    if (alnumRun.length >= 2) out.add(alnumRun);
    alnumRun = "";
  };
  for (const ch of s) {
    if (CJK.test(ch)) {
      flushAlnum();
      cjkRun += ch;
    } else if (/[a-z0-9]/.test(ch)) {
      flushCjk();
      alnumRun += ch;
    } else {
      flushCjk();
      flushAlnum();
    }
  }
  flushCjk();
  flushAlnum();
  for (const g of GENERIC_TOKENS) out.delete(g);
  return out;
}

/** 在售产品 + AI 按产品给的词 → 词表。 */
export function buildProductVocab(
  products: readonly ProductVocabInput[],
  keywordsByProduct?: Record<string, readonly string[]> | null
): Set<string> {
  const vocab = new Set<string>();
  const add = (t: string | null | undefined) => tokenize(t).forEach((x) => vocab.add(x));
  for (const p of products) {
    add(p.name);
    add(p.summary);
  }
  for (const words of Object.values(keywordsByProduct ?? {})) {
    for (const w of words ?? []) add(w);
  }
  return vocab;
}

/**
 * 这个词和在售产品有没有字面重合。
 * @returns `true` 有重合 / `false` 没有(该提示)/ `null` 判不了(词表空或词里全是泛词)—— 不提示。
 */
export function isKeywordRelevant(keyword: string, vocab: ReadonlySet<string>): boolean | null {
  if (vocab.size === 0) return null;
  const tokens = tokenize(keyword);
  if (tokens.size === 0) return null;
  for (const t of tokens) if (vocab.has(t)) return true;
  return false;
}
